import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Logo } from "@/components/domain/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, LogIn, ArrowLeft, Loader2, Mail, Lock } from "lucide-react";
import { toast } from "sonner";

const DEST = { superadmin: "/plataforma", tenant_owner: "/admin", admin: "/admin", supervisor: "/admin", validator: "/admin/validacion", analyst: "/admin", seller: "/vendedor" };

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && user) nav(DEST[user.role] || "/admin"); }, [user, loading, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Ingresa correo y contraseña"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data);
      toast.success(`Bienvenido, ${r.data.name}`);
      nav(DEST[r.data.role] || "/admin");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo iniciar sesión");
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    if (!email) { toast.error("Escribe tu correo arriba y vuelve a intentar"); return; }
    try { await api.post("/auth/forgot-password", { email }); } catch (err) { console.error("forgot-password failed", err); }
    toast.success("Si el correo existe, te enviamos un enlace para restablecer tu contraseña");
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="hidden flex-col justify-between trust-gradient p-12 text-white lg:flex">
        <Logo dark />
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight">Opera tus sorteos con transparencia y control.</h1>
          <p className="mt-4 max-w-md text-slate-300">Panel de administración, validación de pagos, ganadores y reportes. Todo aislado por empresa y auditado.</p>
          <div className="mt-8 space-y-2 text-sm text-slate-300">
            {["Validación de pagos con auditoría", "Aislamiento por empresa", "Permisos por rol en servidor"].map((t) => (
              <div key={t} className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" />{t}</div>
            ))}
          </div>
        </div>
        <span className="text-xs text-slate-500">© 2026 Círculo Dorado · Rifas y Sorteos</span>
      </div>

      <div className="flex flex-col justify-center px-6 py-12 sm:px-16">
        <Link to="/" className="mb-8 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand"><ArrowLeft className="h-4 w-4" />Volver al sitio</Link>
        <div className="lg:hidden mb-8"><Logo /></div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Acceso operadores</h2>
        <p className="mt-2 text-slate-500">Ingresa con tu correo y contraseña.</p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input type="email" placeholder="correo@empresa.com" value={email} onChange={(e) => setEmail(e.target.value)}
              className="pl-10" data-testid="login-email" autoComplete="username" />
          </div>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input type="password" placeholder="Contraseña" value={password} onChange={(e) => setPassword(e.target.value)}
              className="pl-10" data-testid="login-password" autoComplete="current-password" />
          </div>
          <Button type="submit" size="lg" disabled={busy} className="w-full rounded-full bg-brand hover:bg-brand-hover" data-testid="login-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LogIn className="mr-2 h-4 w-4" />}Iniciar sesión
          </Button>
        </form>

        <button type="button" onClick={forgot} className="mt-4 text-left text-xs text-brand hover:underline" data-testid="forgot-password-link">¿Olvidaste tu contraseña?</button>
        <p className="mt-6 text-xs text-slate-400">Permisos validados en el servidor. El acceso es solo para operadores autorizados.</p>
      </div>
    </div>
  );
}
